"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flowService } from "@/api/flow";
import { useIntegrations } from "@/api/integration";
import type { Flow, FlowChange, FlowPending, FlowTrigger, Gym, Integration } from "@/api/types";
import type {
  FlowNode,
  FlowNodeDefinition,
  FlowNodeRoot,
  NullableFlowNode,
} from "@/api/types/flow";
import type { SendMessageActionOptions } from "@/api/types/automation";
import { useMatchMutate } from "@/ui/hooks/match-mutate";
import {
  type StepAddMethod,
  trackStepAdded,
  trackStepRemoved,
  trackStepUpdated,
} from "../analytics";
import {
  applyDeliveryWindowToActions,
  deriveFlowDeliveryWindow,
  isSendMessageAction,
} from "./delivery-window";
import { type ValidationIssue, validateFlow } from "./validation";

/**
 * What the right-hand inspector is currently editing. Nodes are selected from
 * the canvas; entry triggers are selected from the canvas trigger card. Exit
 * triggers are managed entirely in the left settings panel and never set this.
 */
export type FlowSelection = { type: "node"; id: string } | { type: "trigger"; id: string };

interface FlowEditorContextValue {
  flow: Flow;
  gym: Gym;
  pending: FlowPending | undefined;
  node: FlowNodeRoot;
  triggers: FlowTrigger[];
  exitTriggers: FlowTrigger[];
  readOnly: boolean;
  validationErrors: Map<string, ValidationIssue[]>;
  saveAttempted: boolean;
  installedIntegrations: Integration[];
  handleChange: (change: FlowChange) => Promise<void>;
  setSaveAttempted: (v: boolean) => void;
  selection: FlowSelection | null;
  setSelection: (selection: FlowSelection | null) => void;
  selectedNode: FlowNode | null;
  selectedTrigger: FlowTrigger | null;
  /**
   * Insert a node and auto-select the newly created node so its editor opens in
   * the inspector. The new node id is generated server-side, so we diff the
   * tree before/after the change to discover it (no API change required).
   */
  insertNode: (
    targetNode: string,
    index: number,
    definition: FlowNodeDefinition,
    method?: StepAddMethod,
  ) => Promise<void>;
}

function collectNodeIds(node: NullableFlowNode, acc: Set<string> = new Set()): Set<string> {
  if (!node) return acc;
  acc.add(node.id);
  for (const child of node.children ?? []) collectNodeIds(child as NullableFlowNode, acc);
  return acc;
}

function findNodeById(node: NullableFlowNode, id: string): FlowNode | null {
  if (!node) return null;
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findNodeById(child as NullableFlowNode, id);
    if (found) return found;
  }
  return null;
}

const FlowEditorContext = createContext<FlowEditorContextValue | null>(null);

export function useFlowEditor() {
  const ctx = useContext(FlowEditorContext);
  if (!ctx) throw new Error("useFlowEditor must be used within FlowEditorProvider");
  return ctx;
}

export function FlowEditorProvider({
  flow,
  gym,
  readOnly = false,
  children,
}: {
  flow: Flow;
  gym: Gym;
  readOnly?: boolean;
  children: React.ReactNode;
}) {
  const matchMutate = useMatchMutate();
  const [saveAttempted, setSaveAttempted] = useState(false);

  const { data: integrations } = useIntegrations({ gym: gym.id });
  const installedIntegrations = useMemo(() => integrations ?? [], [integrations]);

  const pending = flow.pending;

  const node = readOnly ? flow.node : (pending?.node ?? flow.node);
  const triggerCollection = readOnly
    ? flow.triggers
    : ((pending?.triggers ?? flow.triggers) as FlowTrigger[]);
  const triggers = triggerCollection.filter((trigger) => trigger.side !== "exit");
  const exitTriggers = triggerCollection.filter((trigger) => trigger.side === "exit");

  const validationErrors = useMemo(
    () => (readOnly ? new Map<string, ValidationIssue[]>() : validateFlow(pending)),
    [pending, readOnly],
  );

  const [selection, setSelection] = useState<FlowSelection | null>(null);
  // Holds the set of node ids that existed just before an insert, so the effect
  // below can find the newly created node once the tree refetches.
  const pendingSelectPrevIdsRef = useRef<Set<string> | null>(null);

  const handleChange = useCallback(
    async (change: FlowChange) => {
      if (readOnly) return;
      // Editing the structure invalidates any selection that points at removed
      // entities; clear eagerly on lifecycle transitions for snappiness.
      if (change.type === "discard" || change.type === "commit") {
        setSelection(null);
      }
      await flowService.change(flow.id, change);
      await matchMutate(/\/flows/);
      if (change.type === "update_node") {
        trackStepUpdated({ gym: gym.name, flow: flow.name, definition: change.options.node });
      } else if (change.type === "remove_node") {
        trackStepRemoved({ gym: gym.name, flow: flow.name });
      }
    },
    [flow.id, flow.name, gym.name, matchMutate, readOnly],
  );

  const insertNode = useCallback(
    async (
      targetNode: string,
      index: number,
      definition: FlowNodeDefinition,
      method: StepAddMethod = "insert",
    ) => {
      if (readOnly) return;
      pendingSelectPrevIdsRef.current = collectNodeIds(node);
      await handleChange({
        type: "insert_node",
        options: { targetNode, index, node: definition },
      });
      trackStepAdded({ gym: gym.name, flow: flow.name, definition, method });
    },
    [readOnly, node, handleChange, gym.name, flow.name],
  );

  const selectedNode = useMemo(
    () => (selection?.type === "node" ? findNodeById(node, selection.id) : null),
    [selection, node],
  );
  const selectedTrigger = useMemo(
    () =>
      selection?.type === "trigger"
        ? ([...triggers, ...exitTriggers].find((trigger) => trigger.id === selection.id) ?? null)
        : null,
    [selection, triggers, exitTriggers],
  );

  // Once the tree refetches after an insert, select the node that wasn't there
  // before so its editor opens in the inspector. Newly added message steps also
  // inherit the flow's delivery window (if one is configured) so the per-flow
  // setting stays consistent without a manual re-save.
  useEffect(() => {
    const prev = pendingSelectPrevIdsRef.current;
    if (!prev) return;
    pendingSelectPrevIdsRef.current = null;
    const currentIds = collectNodeIds(node);
    for (const id of currentIds) {
      if (prev.has(id)) continue;
      setSelection({ type: "node", id });

      const newNode = findNodeById(node, id);
      if (newNode?.type !== "action") return;
      const messageActions = newNode.options.actions.filter(isSendMessageAction);
      if (messageActions.length === 0) return;
      const alreadyHasWindow = messageActions.some(
        (action) => (action.options as SendMessageActionOptions).deliveryWindow != null,
      );
      if (alreadyHasWindow) return;

      const window = deriveFlowDeliveryWindow(node);
      if (!window?.enabled) return;
      void handleChange({
        type: "update_node",
        options: {
          targetNode: id,
          node: {
            type: "action",
            options: {
              actions: applyDeliveryWindowToActions(newNode.options.actions, window),
            },
          },
        },
      });
      return;
    }
  }, [node, handleChange]);

  // Drop a selection whose target no longer exists (removed, discarded, etc.).
  useEffect(() => {
    if (selection?.type === "node" && !selectedNode) setSelection(null);
    if (selection?.type === "trigger" && !selectedTrigger) setSelection(null);
  }, [selection, selectedNode, selectedTrigger]);

  const value = useMemo<FlowEditorContextValue>(
    () => ({
      flow,
      gym,
      pending,
      node,
      triggers,
      exitTriggers,
      readOnly,
      validationErrors,
      saveAttempted,
      installedIntegrations,
      handleChange,
      setSaveAttempted,
      selection,
      setSelection,
      selectedNode,
      selectedTrigger,
      insertNode,
    }),
    [
      flow,
      gym,
      pending,
      node,
      triggers,
      exitTriggers,
      readOnly,
      validationErrors,
      saveAttempted,
      installedIntegrations,
      handleChange,
      selection,
      selectedNode,
      selectedTrigger,
      insertNode,
    ],
  );

  return <FlowEditorContext.Provider value={value}>{children}</FlowEditorContext.Provider>;
}
